import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Alert,
  TextField,
  useMediaQuery,
  IconButton,
  Chip,
  Card,
  CardContent,
  Typography,
  Grid,
  Divider,
  Tooltip,
} from "@mui/material";
import { useSelector } from "react-redux";
import { RootState } from "@/store/type";
import { isUndefined } from "lodash";
import CompanyWarningPopup from "../../../../app/features/CompanyCreate/companycreatePopup";
import { useNavigate } from "react-router-dom";
import StatusTabs from "../../../order/components/view/status.tabs";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableComponent from "@/app/global-components/table";
import moment from "moment";
import { useTranslate } from "@/locales";
import { useLocale } from "@/middleware";
import { LANG, ROLE } from "@/types";
import { routes } from "@/consts";
import useOrder from "@/hooks/use-order";
import useRegisterCompany from "@/hooks/use-register-company";
import {
  ShoppingCart,
  Business,
  CalendarToday,
  LocationOn,
  AttachMoney,
  FilterList,
} from "@mui/icons-material";

interface Product {
  itemId: string;
  item: string;
  quantity: number;
  price: number;
}

interface Order {
  id: string;
  title: string;
  deliverDate: string;
  address: string;
  products: Product[];
  Sendedproducts: Product[];
  OrderNr: number;
  status: string;
  tender: { title: string };
  company: {
    CompanyNr: string;
    nameAr: string;
  };
}

const OrderBuylist: React.FC = () => {
  const { locale } = useLocale();
  const { t } = useTranslate(locale);
  const navigate = useNavigate();
  const myCompany = useSelector((state: RootState) => state.auth.myCompany);
  const { loading, error, fetchBuyerOrders } = useOrder();
  const [orders, setOrders] = useState<Order[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const itemsPerPage = 10;
  const [isCompanyAvailable, setIsCompanyAvailable] = useState(false);
  const { getCompanyMine } = useRegisterCompany();

  useEffect(() => {
    const fetchCompany = async () => {
      await getCompanyMine().catch(() => {
        setIsCompanyAvailable(true);
      });
    };
    fetchCompany();
  }, []);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const authUser = useSelector((state: RootState) => state.auth.authUser);
  const userRole = isUndefined(authUser?.role) ? "" : authUser?.role;
  const isMobile = useMediaQuery("(max-width:600px)");
  const isTablet = useMediaQuery("(max-width:960px)");

  const [statuses, setStatuses] = useState([
    { label: t("status.planning"), name: "Planning", total: 0 },
    { label: t("general.purchaseRequest"), name: "Opened", total: 0 },
    {
      label: t("buyer_pages.return_request_details.order"),
      name: "Accepted",
      total: 0,
    },
    {
      label: t("status.receivingManagement"),
      name: "Sending",
      part: "PARTSENDING",
      total: 0,
    },
    { label: t("status.canceled"), name: "Canceled", total: 0 },
    { label: t("status.finished"), name: "Finished", total: 0 },
  ]);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        if (myCompany?.id) {
          const result = await fetchBuyerOrders(myCompany.id);
          setOrders(result?.data || []);
        }
      } catch (err) {
        console.error("Error fetching orders:", err);
      }
    };
    fetchOrders();
  }, [myCompany?.id]);

  useEffect(() => {
    const updatedStatuses = statuses.map((status) => ({
      ...status,
      total: orders.filter(
        (order) => order.status === status.name || order.status === status?.part
      ).length,
    }));
    setStatuses(updatedStatuses);
  }, [orders]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const filteredOrders = orders
    .filter((order) => {
      if (selectedStatus) {
        return (
          order.status === selectedStatus ||
          (order.status === "PARTSENDING" && selectedStatus == "Sending")
        );
      }
      return true;
    })
    .filter((order) => {
      const searchLower = searchTerm.toLowerCase();
      return (
        order?.OrderNr?.toString().includes(searchLower) ||
        order.title.toLowerCase().includes(searchLower) ||
        order.company?.CompanyNr.toLowerCase().includes(searchLower) ||
        order.tender?.title.toLowerCase().includes(searchLower)
      );
    });

  const calculateTotalPrice = (products: Product[]) =>
    products.reduce(
      (sum, product) => sum + product.price * product.quantity,
      0
    );

  const getStatusLabel = (status: string) => {
    const statusItem = statuses.find((s) => s.name === status);
    return statusItem ? statusItem.label : t("status.unknown");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Planning":
        return "default";
      case "Opened":
        return "primary";
      case "Accepted":
        return "success";
      case "Sending":
      case "PARTSENDING":
        return "warning";
      case "Receiving":
        return "info";
      case "Revision":
        return "secondary";
      case "Canceled":
        return "error";
      default:
        return "default";
    }
  };

  const handleNavigateToOrder = (orderId: string) => {
    navigate(`${routes.order}/${orderId}`);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: string) => {
    setSelectedStatus(newValue);
  };

  const columns = [
    { title: t("orders.number"), field: "OrderNr" },
    {
      title: t("general.tender"),
      field: "tender.title",
      render: (row: Order) => row.tender?.title || "N/A",
      hideOnMobile: true,
    },
    {
      title: t("company.fields.name"),
      field: "company.nameAr",
      render: (row: Order) => row.company?.CompanyNr || "N/A",
    },
    { title: t("orders.title"), field: "title" },
    {
      title: t("orders.fields.deliveryDate"),
      field: "deliverDate",
      hideOnMobile: true,
      render: (row: Order) => moment(row.deliverDate).format("YYYY-MM-DD"),
    },
    {
      title: t("company.fields.address"),
      field: "address",
      hideOnMobile: true,
    },
    {
      title: t("orders.fields.totalPrice"),
      field: "totalPrice",
      render: (row: Order) =>
        `${calculateTotalPrice(row.products).toFixed(2)} ${t("currency")}`,
    },
    {
      title: t("orders.fields.status"),
      field: "status",
      render: (row: Order) => getStatusLabel(row.status),
    },
    {
      title: t("general.actions"),
      field: "actions",
      render: (row: Order) => (
        <IconButton
          color="inherit"
          size="small"
          onClick={() => handleNavigateToOrder(row.id)}
          title={t("general.view")}
        >
          <VisibilityIcon />
        </IconButton>
      ),
    },
  ];

  const handleRowClick = (row: Order) => {
    handleNavigateToOrder(row.id);
  };

  const paginatedOrders = filteredOrders;

  const renderOrderCard = (order: Order) => (
    <Card
      key={order.id}
      onClick={() => handleNavigateToOrder(order.id)}
      sx={{
        cursor: "pointer",
        transition: "all 0.3s ease",
        border: "1px solid #e5e7eb",
        borderRadius: 3,
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 8px 25px rgba(0, 0, 0, 0.15)",
          borderColor: "#644980",
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 600, color: "#1f2937", mb: 1 }}
            >
              {order.title}
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b7280", mb: 1 }}>
              #{order.OrderNr}
            </Typography>
          </Box>
          <Chip
            label={getStatusLabel(order.status)}
            color={getStatusColor(order.status) as any}
            size="small"
            sx={{ fontWeight: 500 }}
          />
        </Box>

        <Divider sx={{ my: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <Business sx={{ fontSize: 16, color: "#6b7280", mr: 1 }} />
              <Typography variant="body2" sx={{ color: "#374151" }}>
                {order.company?.CompanyNr || "N/A"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <ShoppingCart sx={{ fontSize: 16, color: "#6b7280", mr: 1 }} />
              <Typography variant="body2" sx={{ color: "#374151" }}>
                {order.tender?.title || "N/A"}
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <CalendarToday sx={{ fontSize: 16, color: "#6b7280", mr: 1 }} />
              <Typography variant="body2" sx={{ color: "#374151" }}>
                {moment(order.deliverDate).format("MMM DD, YYYY")}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
              <LocationOn sx={{ fontSize: 16, color: "#6b7280", mr: 1 }} />
              <Typography variant="body2" sx={{ color: "#374151" }} noWrap>
                {order.address}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AttachMoney sx={{ fontSize: 18, color: "#059669", mr: 0.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, color: "#059669" }}>
              {calculateTotalPrice(order.products).toFixed(2)} {t("currency")}
            </Typography>
          </Box>
          <Tooltip title={t("general.view")}>
            <IconButton
              size="small"
              sx={{
                backgroundColor: "#f3f4f6",
                color: "#6b7280",
                "&:hover": {
                  backgroundColor: "#e5e7eb",
                  color: "#374151",
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box
      sx={{
        py: 4,
        px: { xs: 2, sm: 3, md: 4 },
        background:
          "linear-gradient(135deg, #f8fafc 0%, #ffffff 50%, #f1f5f9 100%)",
        minHeight: "100vh",
      }}
    >
      {isCompanyAvailable && <CompanyWarningPopup open={true} />}

      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <Box
            sx={{
              width: 4,
              height: 32,
              background: "linear-gradient(135deg, #644980 0%, #1d4ed8 100%)",
              borderRadius: 2,
              mr: 2,
            }}
          />
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              background: "linear-gradient(135deg, #1f2937 0%, #374151 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
            }}
          >
            {t("orders.listTitle")}{" "}
            {userRole === ROLE.SELLER
              ? t("general.sales")
              : t("general.Purchases")}
          </Typography>
        </Box>

        <Typography variant="body1" sx={{ color: "#6b7280", ml: 6 }}>
          {t("statistics.ordersFound")}: {orders.length}
        </Typography>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", my: 6 }}>
          <CircularProgress size={48} />
        </Box>
      )}

      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 3,
            borderRadius: 2,
            "& .MuiAlert-icon": { fontSize: 24 },
          }}
        >
          {t("alerts.error.fetching")}{" "}
          {userRole === ROLE.SELLER
            ? t("general.sales")
            : t("general.Purchases")}
          .
        </Alert>
      )}

      {!loading && !error && (
        <>
          {/* Status Tabs */}
          <Box sx={{ mb: 4 }}>
            <StatusTabs
              statuses={statuses}
              selectedStatus={selectedStatus}
              handleTabChange={handleTabChange}
              orderTotal={orders.length}
            />
          </Box>

          {/* Search and Filters */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: 2,
              mb: 4,
              alignItems: { xs: "stretch", sm: "center" },
            }}
          >
            <TextField
              label={t("actions.searching")}
              variant="outlined"
              fullWidth
              dir={locale === LANG.AR ? "rtl" : "ltr"}
              value={searchTerm}
              size="small"
              onChange={handleSearchChange}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon sx={{ color: "#6b7280" }} />
                  </InputAdornment>
                ),
                inputProps: { dir: locale === LANG.AR ? "rtl" : "ltr" },
              }}
              sx={{
                maxWidth: { xs: "100%", sm: 400 },
                "& .MuiOutlinedInput-root": {
                  borderRadius: 2,
                  backgroundColor: "#ffffff",
                  "&:hover": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                    },
                  },
                  "&.Mui-focused": {
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: "#644980",
                      borderWidth: 2,
                    },
                  },
                },
                "& .MuiInputBase-root": {
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                },
                "& .MuiInputLabel-root": {
                  transformOrigin: locale === LANG.AR ? "right" : "left",
                  direction: locale === LANG.AR ? "rtl" : "ltr",
                  ...(locale === LANG.AR
                    ? { right: 30, left: "auto" }
                    : { left: 0, right: "auto" }),
                },
                "& legend": {
                  textAlign: locale === LANG.AR ? "end" : "start",
                },
              }}
            />

            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <FilterList sx={{ color: "#6b7280" }} />
              <Typography variant="body2" sx={{ color: "#6b7280" }}>
                {filteredOrders.length} {t("general.items")}
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          {isMobile || isTablet ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
              {paginatedOrders.length > 0 ? (
                paginatedOrders.map(renderOrderCard)
              ) : (
                <Card sx={{ p: 4, textAlign: "center", borderRadius: 3 }}>
                  <ShoppingCart
                    sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }}
                  />
                  <Typography variant="h6" sx={{ color: "#6b7280", mb: 1 }}>
                    {t("orders.noOrdersFound")}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#9ca3af" }}>
                    {t("orders.noOrdersDescription")}
                  </Typography>
                </Card>
              )}
            </Box>
          ) : (
            <Card
              sx={{ borderRadius: 3, boxShadow: "none", overflow: "hidden" }}
            >
              <TableComponent
                columns={columns}
                data={paginatedOrders}
                rowKeyField="id"
                isMobile={isMobile}
                maxHeight={600}
                pagination={true}
                rowsPerPage={itemsPerPage}
                onRowClick={handleRowClick}
              />
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

export default OrderBuylist;
